import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import api from '../services/api';
import packageJson from '../../package.json';

const AboutPage = ({ onBack }) => {
  const [backendVersion, setBackendVersion] = useState(null);

  useEffect(() => {
    api.get('/version')
      .then(res => setBackendVersion(res.data.version))
      .catch(() => setBackendVersion('unavailable'));
  }, []);

  return (
    <div className="about-page" style={{ padding: '40px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <SEO 
        title="About Study Centro" 
        description="Learn more about Study Centro, the ultimate learning workspace for students and educators." 
      />
      
      <header style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '80px', 
          height: '80px', 
          background: 'var(--primary)', 
          color: 'white', 
          borderRadius: '20px', 
          fontSize: '2.5rem', 
          marginBottom: '20px',
          boxShadow: 'var(--shadow-lg)'
        }}>
          <i className="fas fa-graduation-cap" />
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--text-main)', marginBottom: '12px' }}>About Study Centro</h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)', maxWidth: '600px', margin: '0 auto' }}>
          Your all-in-one workspace for academic excellence, designed to streamline study workflows and exam preparation.
        </p>
      </header>

      <section className="premium-card" style={{ marginBottom: '32px', padding: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <i className="fas fa-code-branch" style={{ color: 'var(--primary)' }} />
          Developer Information
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div style={{ 
            width: '100px', 
            height: '100px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, var(--primary), var(--accent))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '2.5rem',
            fontWeight: 800,
            boxShadow: 'var(--shadow-md)'
          }}>
            PC
          </div>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 4px' }}>Pradeep Cheekatla</h3>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 12px', fontSize: '0.95rem' }}>Lead Developer & Architect</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <a href="https://github.com/pradeepcheekatla" target="_blank" rel="noreferrer" style={{ color: 'var(--text-main)', fontSize: '1.2rem' }}>
                <i className="fab fa-github" />
              </a>
              <a href="mailto:pradeepcheekatla@gmail.com" style={{ color: 'var(--text-main)', fontSize: '1.2rem' }}>
                <i className="fas fa-envelope" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="premium-card" style={{ padding: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <i className="fas fa-bullseye" style={{ color: 'var(--primary)' }} />
          Our Mission
        </h2>
        <p style={{ lineHeight: '1.7', color: 'var(--text-main)', fontSize: '1.05rem' }}>
          Study Centro was built with a single goal in mind: to empower students by providing a modern, 
          intuitive platform for managing academic content. From interactive MCQ tests to seamless 
          PDF sharing and folder-based organization, we believe that the right tools can 
          transform the learning experience.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '32px' }}>
          <div style={{ padding: '16px', background: 'var(--bg-main)', borderRadius: '12px', textAlign: 'center' }}>
            <i className="fas fa-bolt" style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 4px' }}>Fast</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>Optimized for speed and productivity.</p>
          </div>
          <div style={{ padding: '16px', background: 'var(--bg-main)', borderRadius: '12px', textAlign: 'center' }}>
            <i className="fas fa-shield-alt" style={{ color: 'var(--success)', fontSize: '1.5rem', marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 4px' }}>Secure</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>Your data and content are protected.</p>
          </div>
          <div style={{ padding: '16px', background: 'var(--bg-main)', borderRadius: '12px', textAlign: 'center' }}>
            <i className="fas fa-mobile-alt" style={{ color: 'var(--primary)', fontSize: '1.5rem', marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 4px' }}>Responsive</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>Works perfectly on all devices.</p>
          </div>
        </div>
      </section>

      <footer style={{ marginTop: '48px', textAlign: 'center' }}>
        <button className="secondary" onClick={onBack} style={{ padding: '12px 32px' }}>
          <i className="fas fa-arrow-left" style={{ marginRight: '8px' }} /> Back to Workspace
        </button>
        <p style={{ marginTop: '24px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          © {new Date().getFullYear()} Study Centro. Created with ❤️ by Pradeep Cheekatla.
        </p>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '16px', 
          marginTop: '8px', 
          fontSize: '0.8rem', 
          color: 'var(--text-muted)',
          flexWrap: 'wrap'
        }}>
          <span>Frontend v{packageJson.version}</span>
          <span style={{ opacity: 0.4 }}>|</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            Server v{backendVersion || '…'}
            <span style={{
              display: 'inline-block',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: backendVersion && backendVersion !== 'unavailable' 
                ? 'var(--success, #22c55e)' 
                : backendVersion === 'unavailable' 
                  ? 'var(--danger, #ef4444)' 
                  : 'var(--text-muted)',
              flexShrink: 0
            }} title={backendVersion === 'unavailable' ? 'Server unreachable' : 'Server online'} />
          </span>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
